import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from '../../context/SocketContext';

function useDeviceTelemetry(device) {
  const socket = useSocket();
  const deviceId = Number(device?.id || 0);
  const requestSequence = useRef(0);
  const [snapshot, setSnapshot] = useState({ tvInputManager: null, bluetoothAudio: null });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [settingsStatus, setSettingsStatus] = useState('idle');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsRequestId, setSettingsRequestId] = useState(null);

  const nextRequestId = useCallback((prefix) => {
    requestSequence.current += 1;
    return `${prefix}-${deviceId}-${Date.now()}-${requestSequence.current}`;
  }, [deviceId]);

  const refresh = useCallback(() => {
    if (!socket || !deviceId) return;
    const id = nextRequestId('site-control-telemetry');
    setStatus('loading');
    setMessage('Retrieving device telemetry…');
    setRequestId(id);
    socket.emit('sendCommand', { deviceId, command: 'get_state', value: null, id });
  }, [deviceId, nextRequestId, socket]);

  const openBluetoothSettings = useCallback(() => {
    if (!socket || !deviceId) return;
    const id = nextRequestId('site-control-bluetooth-settings');
    setSettingsStatus('loading');
    setSettingsMessage('Requesting the Android Bluetooth settings page…');
    setSettingsRequestId(id);
    socket.emit('sendCommand', {
      deviceId,
      command: 'open_settings',
      value: { page: 'bluetooth' },
      id,
    });
  }, [deviceId, nextRequestId, socket]);

  useEffect(() => {
    setSnapshot({ tvInputManager: null, bluetoothAudio: null });
    setMessage('');
    setSettingsMessage('');
    if (deviceId) refresh();
  }, [deviceId, refresh]);

  useEffect(() => {
    if (!socket || !deviceId) return undefined;
    const handleResponse = (payload = {}) => {
      if (Number(payload.deviceId || deviceId) !== deviceId) return;
      if (payload.type === 'open_settings_response' && payload.requestId === settingsRequestId) {
        setSettingsStatus(payload.success ? 'success' : 'error');
        setSettingsMessage(payload.success
          ? payload.fallback
            ? 'Bluetooth-specific settings were unavailable; Android opened general Settings instead.'
            : 'Android reports that the Bluetooth settings page was opened.'
          : `Bluetooth settings could not be opened: ${payload.error || 'unknown error'}.`);
        setSettingsRequestId(null);
        return;
      }
      if (payload.type === 'tv_input_manager_update' && payload.tvInputManager) {
        setSnapshot((current) => ({ ...current, tvInputManager: payload.tvInputManager }));
        return;
      }
      if (payload.type !== 'state_response' || payload.requestId !== requestId) return;
      const state = payload.state || {};
      setSnapshot({
        tvInputManager: state.tvInputManager || null,
        bluetoothAudio: state.bluetoothAudio || null,
      });
      setStatus('success');
      setMessage('Device telemetry retrieved.');
      setRequestId(null);
    };
    const handleUnavailable = (payload = {}) => {
      if (Number(payload.deviceId) !== deviceId) return;
      if (payload.requestId === settingsRequestId) {
        setSettingsStatus('error');
        setSettingsMessage('The selected device is not connected to its native Android command channel.');
        setSettingsRequestId(null);
      }
      if (payload.requestId === requestId) {
        setStatus('error');
        setMessage('The selected device is not connected to its native Android command channel.');
        setRequestId(null);
      }
    };
    socket.on('deviceCommandResponse', handleResponse);
    socket.on('deviceNotConnected', handleUnavailable);
    return () => {
      socket.off('deviceCommandResponse', handleResponse);
      socket.off('deviceNotConnected', handleUnavailable);
    };
  }, [deviceId, requestId, settingsRequestId, socket]);

  return {
    bluetoothAudio: snapshot.bluetoothAudio,
    message,
    openBluetoothSettings,
    refresh,
    settingsMessage,
    settingsStatus,
    status,
    tvInputManager: snapshot.tvInputManager,
  };
}

export default useDeviceTelemetry;
